import CheckmarkIcon from '@listening/assets/astro/icons/new-design-25/browser-ext/checkmark.svg?react';
import { CtaButton } from '@/components/astro/cta-button/cta-button';

// Add props for button text and link
type StartListeningCTAProps = {
  buttonText?: string;
  buttonLink?: string;
};

export function StartListeningCTA({
  buttonText,
  buttonLink,
}: StartListeningCTAProps) {
  // Fallbacks
  const fallbackText = 'Add to your browser';
  const fallbackLink =
    'https://chromewebstore.google.com/detail/listeningcom/amjjglmomeodglgclcnjdjbhhcfnbnpd';

  return (
    <section
      aria-labelledby='cta-heading'
      className='bg-linear-to-br from-blue-600 to-purple-700 py-16 lg:py-24'
      itemScope
      itemType='https://schema.org/SoftwareApplication'
    >
      <div className='container mx-auto px-4 lg:px-8'>
        <div className='mx-auto max-w-4xl text-center'>
          <header className='mb-12'>
            <h2
              className='mb-6 text-3xl font-bold text-white lg:text-5xl'
              id='cta-heading'
              itemProp='name'
            >
              Start Listening to the Web Today
            </h2>
            <p
              className='mx-auto mb-8 max-w-3xl text-xl text-blue-100 lg:text-2xl'
              itemProp='description'
            >
              Join thousands of users who are already transforming their reading
              experience. Install the extension and turn any webpage into your
              personal audiobook.
            </p>
          </header>

          <div className='mb-12 flex flex-col items-center justify-center gap-4 sm:flex-row'>
            <CtaButton
              className='rounded-full bg-royal-50 px-8 py-7 text-xl font-light text-white hover:bg-black/80 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
              href={buttonLink ?? fallbackLink}
              label={buttonText ?? fallbackText}
              variant='gradient'
            />
          </div>

          <div
            aria-label='Usage statistics'
            className='mb-12 grid grid-cols-1 gap-8 md:grid-cols-3'
            itemScope
            itemType='https://schema.org/ItemList'
          >
            <div
              className='text-center'
              itemProp='itemListElement'
              itemScope
              itemType='https://schema.org/ListItem'
            >
              <meta content='1' itemProp='position' />
              <div
                className='mb-2 text-3xl font-bold text-white'
                itemProp='item'
                itemScope
                itemType='https://schema.org/QuantitativeValue'
              >
                <span itemProp='value'>50K+</span>
              </div>
              <div className='text-blue-100' itemProp='name'>
                Active Users
              </div>
            </div>
            <div
              className='text-center'
              itemProp='itemListElement'
              itemScope
              itemType='https://schema.org/ListItem'
            >
              <meta content='2' itemProp='position' />
              <div
                className='mb-2 text-3xl font-bold text-white'
                itemProp='item'
                itemScope
                itemType='https://schema.org/QuantitativeValue'
              >
                <span itemProp='value'>5+</span>
              </div>
              <div className='text-blue-100' itemProp='name'>
                Languages Supported
              </div>
            </div>
            <div
              className='text-center'
              itemProp='itemListElement'
              itemScope
              itemType='https://schema.org/ListItem'
            >
              <meta content='3' itemProp='position' />
              <div
                className='mb-2 text-3xl font-bold text-white'
                itemProp='item'
                itemScope
                itemType='https://schema.org/QuantitativeValue'
              >
                <span itemProp='value'>1M+</span>
              </div>
              <div className='text-blue-100' itemProp='name'>
                Pages Converted
              </div>
            </div>
          </div>

          <article
            aria-labelledby='features-heading'
            className='rounded-2xl border border-white/20 bg-white/10 p-8 backdrop-blur-sm'
            itemScope
            itemType='https://schema.org/ItemList'
          >
            <h3
              className='mb-6 text-2xl font-bold text-white'
              id='features-heading'
              itemProp='name'
            >
              What You Get
            </h3>
            <ul
              aria-label='Extension features'
              className='grid grid-cols-1 gap-6 text-left md:grid-cols-2'
            >
              <li
                className='flex items-center space-x-3'
                itemProp='itemListElement'
                itemScope
                itemType='https://schema.org/ListItem'
              >
                <meta content='1' itemProp='position' />
                <div
                  aria-hidden='true'
                  className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-400'
                >
                  <CheckmarkIcon />
                </div>
                <span
                  className='text-white'
                  itemProp='item'
                  itemScope
                  itemType='https://schema.org/Thing'
                >
                  <span itemProp='name'>
                    Instant text-to-speech on any website
                  </span>
                </span>
              </li>
              <li
                className='flex items-center space-x-3'
                itemProp='itemListElement'
                itemScope
                itemType='https://schema.org/ListItem'
              >
                <meta content='2' itemProp='position' />
                <div
                  aria-hidden='true'
                  className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-400'
                >
                  <CheckmarkIcon />
                </div>
                <span
                  className='text-white'
                  itemProp='item'
                  itemScope
                  itemType='https://schema.org/Thing'
                >
                  <span itemProp='name'>
                    Natural AI voices with speed control
                  </span>
                </span>
              </li>
              <li
                className='flex items-center space-x-3'
                itemProp='itemListElement'
                itemScope
                itemType='https://schema.org/ListItem'
              >
                <meta content='3' itemProp='position' />
                <div
                  aria-hidden='true'
                  className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-400'
                >
                  <CheckmarkIcon />
                </div>
                <span
                  className='text-white'
                  itemProp='item'
                  itemScope
                  itemType='https://schema.org/Thing'
                >
                  <span itemProp='name'>
                    Document upload and email integration
                  </span>
                </span>
              </li>
              <li
                className='flex items-center space-x-3'
                itemProp='itemListElement'
                itemScope
                itemType='https://schema.org/ListItem'
              >
                <meta content='4' itemProp='position' />
                <div
                  aria-hidden='true'
                  className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-400'
                >
                  <CheckmarkIcon />
                </div>
                <span
                  className='text-white'
                  itemProp='item'
                  itemScope
                  itemType='https://schema.org/Thing'
                >
                  <span itemProp='name'>
                    Cross-device sync and library management
                  </span>
                </span>
              </li>
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}
